import clsx from "clsx";
import { basisLabel } from "../../shared/money";
import { FixHere } from "../../web/components/FixHere";
import { Box } from "../../web/components/ui";
import { useMoney } from "../../web/lib/money";
import { type CostDraft, draftPartCost, itemTax } from "./data";
import { isProduct, type MaterialRow, type PriceListData } from "./meta";
import { underMinimum } from "./pricing";
import { type ItemDraft, type ProfitCase, profitCases } from "./itemDrafts";

/*
 * What an item makes per unit, estimated, before tax: the shaded box at the bottom of its Pricing and Costing tabs, as a small
 * table: what it's charged, what it costs, what that makes, and the markup on its cost, its digits lined up. With a price or cost
 * to update, or its pricing or cost being edited (its window's drafts, on either tab: shown as they'd make it before they're saved),
 * a second row under "In ServiceM8": once updated (what stays the same greyed, so what changes stands out). Under the minimum
 * markup (Price List settings), what counts (once updated, if anything is) is red, saying so.
 */

/** One case's four figures, as shown: its price, its cost, what that makes, and the markup on its cost ("-": can't be told). */
function figuresOf(c: ProfitCase, money: (n: number) => string, percent: (n: number) => string): string[] {
    const cost = c.profit.profitNet === null ? null : c.profit.costNet;
    const made = cost === null ? null : c.priceNet - cost;
    const markup = cost === null || cost <= 0 || made === null ? null : (made / cost) * 100;
    return [money(c.priceNet), cost === null ? "-" : money(cost), made === null ? "-" : money(made), markup === null ? "-" : percent(Math.round(markup))];
}

/** A figure's cell: on the right, its digits lined up. */
const FIGURE = "py-0.5 pl-4 text-right tabular-nums";

export function ProfitBox(
    props: Readonly<{
        data: PriceListData;
        item: MaterialRow;
        /** Its pricing and cost being edited (its window's drafts): shown as they'd make it. */
        draft: ItemDraft | null;
        costDraft: CostDraft | null;
        /** Bumped by Making too little's Show: it's flashed. */
        fixToken?: number;
    }>,
) {
    const { data, item } = props;
    const fmt = useMoney();
    // What it is now: a product's is here (only kept here), an item's in ServiceM8.
    const nowLabel = isProduct(item) ? "Now" : "In ServiceM8";
    const { rate } = itemTax(data, item);
    const costTarget = props.costDraft ? draftPartCost(data, item, props.costDraft) : undefined;
    const { now, next, changes } = profitCases(data, item, props.draft, costTarget);
    const title = `Per unit (${basisLabel(false, rate)})`;
    const min = data.settings.minMarkup;
    const shown = (c: ProfitCase) => figuresOf(c, (n) => fmt.money(n), (n) => fmt.percent(n));

    // What counts is what it'll make (once updated, if anything's to update): red under the minimum, saying so.
    const low = underMinimum(next.profit.markupPct, min);
    const made = next.profit.profitNet ?? 0;
    let short = "";
    if (low && min > 0) short = `under ${fmt.percent(min)} min`;
    else if (low) short = made < 0 ? "a loss" : "makes nothing";

    // A case's row: its label (none, alone), its figures; `was`: the row above, what's the same as it greyed; `counts`: the row the
    // minimum is judged on (its profit and markup red when under it).
    const row = (c: ProfitCase, label: string, opts: { was?: string[]; counts: boolean; updated?: boolean }) => {
        const figures = shown(c);
        const danger = opts.counts && low;
        return (
            <tr key={label} className="font-semibold">
                <th scope="row" className={clsx("max-w-0 truncate py-0.5 text-left text-xs font-normal", opts.updated ? "font-medium text-warning-ink" : "text-muted")}>
                    {label}
                    {danger && short ? <span className="text-danger">{label ? ` · ${short}` : short}</span> : null}
                </th>
                {figures.map((f, i) => (
                    <td key={i} className={clsx(FIGURE, danger && i >= 2 ? "text-danger" : opts.was?.[i] === f && "font-normal text-muted")}>
                        {f}
                    </td>
                ))}
            </tr>
        );
    };

    return (
        <Box shaded className="mt-auto">
            <FixHere token={props.fixToken} className="-mx-2">
                <div className="rounded-md px-2 py-1">
                    {next.profit.profitNet === null && now.profit.profitNet === null ? (
                        <>
                            <h3 className="box-title m-0">{title}</h3>
                            <p className="m-0 mt-1 text-sm text-muted">No cost yet.</p>
                        </>
                    ) : (
                        // One table (its title on its column headings' line), so every column lines up.
                        <table className="w-full border-collapse">
                            <thead>
                                <tr>
                                    <th scope="col" className="box-title text-left whitespace-nowrap">
                                        {title}
                                    </th>
                                    {["Price", "Cost", "Profit", "Markup"].map((h) => (
                                        <th key={h} scope="col" className={clsx(FIGURE, "text-xs font-normal text-muted")}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {changes
                                    ? [row(now, nowLabel, { counts: false }), row(next, "Once updated", { was: shown(now), counts: true, updated: true })]
                                    : row(now, nowLabel, { counts: true })}
                            </tbody>
                        </table>
                    )}
                </div>
            </FixHere>
        </Box>
    );
}
